import type React from "react";
import { useEffect, useMemo } from "react";

import { InfoCardEnrichment } from "@similarweb/ui-components";

import { i18nFilter } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import { AssetsService } from "app/services/AssetsService";
import { useSiteLobInfo } from "app/services/sitesService/hooks/useSiteLobInfo";
import { transformSiteInfoResponse } from "app/services/sitesService/sitesUtils";
import type { ILobResponse } from "app/services/sitesService/types";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { getFirstFromCommaSeparated, getLobKeyFromLobName, getVerticalFromLobName } from "./utils";
interface ILOBTooltipContentProps {
    domain: string;
    mainDomainOnly?: boolean;
    handleOpenQualificationSideBar?: (e: FixmeType) => void;
    reposition?: () => void;
}
interface ITooltipData {
    title: string;
    displayName: string;
    description: string;
    image: string;
    icons: string[] | string;
    enrichmentCardName: string;
    lastMonthVisitsLabel: string;
    lastMonthVisits: string;
    isCompareMode: boolean;
}
const capitalize = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : "");
function useLobTooltipData(domain: string) {
    const swNavigator = useMemo(() => proNavigator, []);
    const { country, key, keys } = swNavigator.getParams();
    const selectedSite = (keys as string) || (key as string) || domain;
    const finalLobKey = getLobKeyFromLobName(domain);
    const enabled = Boolean(domain && selectedSite && finalLobKey);
    const vertical = getVerticalFromLobName(getFirstFromCommaSeparated(selectedSite)) ?? "";

    const { data: responseData, isLoading } = useSiteLobInfo(
        {
            keys: selectedSite,
            // @ts-ignore FIXME: strict migration - getLobKeyFromLobName returns string | null but ISiteLobInfoParams.lobKey is string — query is gated by enabled flag when finalLobKey is null
            lobKey: finalLobKey,
        },
        undefined,
        {
            enabled,
            staleTime: 60_000,
        },
    );

    const data = useMemo((): ITooltipData | undefined => {
        if (!responseData) {
            return undefined;
        }
        const transformedResponse = transformSiteInfoResponse(responseData);
        const transformedData = transformedResponse as ILobResponse;

        const icons = transformedData.DomainHeaders?.map((header) => header.Icon) ?? [];
        return {
            title: `Business Line • ${capitalize(vertical)}`,
            displayName: transformedData.DomainHeaders?.[0]?.Domain || selectedSite,
            description: transformedData.Description || "",
            image: transformedData.DomainHeaders?.[0]?.Image || "",
            // @ts-ignore FIXME: strict migration - DomainHeaders[].Icon may be undefined; runtime never produces sparse icons
            icons: icons?.length === 1 ? icons[0] : icons,
            enrichmentCardName: transformedData.BusinessLineName || "",
            lastMonthVisitsLabel: i18nFilter()("infotip.monthlyVisits") || "Last Month Visits",
            lastMonthVisits: transformedData.LastMonthVisits
                ? abbrNumberFilter()(transformedData.LastMonthVisits)
                : "-",
            isCompareMode: icons?.length > 1,
        };
    }, [responseData, selectedSite, vertical]);

    return { data, isLoading };
}
export const LOBTooltipContent: React.FC<ILOBTooltipContentProps> = ({
    domain,
    handleOpenQualificationSideBar,
    reposition,
}) => {
    const { data, isLoading } = useLobTooltipData(domain);
    useEffect(() => {
        if (data) {
            reposition?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);
    return (
        <div className="swWebsiteInfoTip">
            <div className="swWebsiteInfoTip-inner">
                <InfoCardEnrichment
                    isLoadingData={isLoading ? !data : undefined}
                    // @ts-ignore FIXME: strict migration - data possibly undefined; isLoadingData branch handles undefined render path
                    title={data?.title}
                    websiteName={data?.displayName}
                    description={
                        !data
                            ? undefined
                            : data.description === ""
                              ? i18nFilter()("infotip.description.undefined")
                              : data.description
                    }
                    // @ts-ignore FIXME: strict migration - data possibly undefined; loading branch suppresses render
                    enrichmentCardName={data?.enrichmentCardName}
                    // @ts-ignore FIXME: strict migration - icons type widened by DomainHeaders mapping; loading branch handles undefined
                    icons={data?.icons || undefined}
                    imgSrc={data?.image || AssetsService.assetUrl("/images/new-no-image.png")}
                    width={handleOpenQualificationSideBar ? 460 : 394}
                />
            </div>
        </div>
    );
};
